import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { syncPush, unlinkPush } from "../lib/push";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const TOKEN_KEY = "ishukart_token";
const USER_KEY = "ishukart_user";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // boot from localStorage and verify
  useEffect(() => {
    const t = localStorage.getItem(TOKEN_KEY);
    const u = localStorage.getItem(USER_KEY);
    if (t && u) {
      setToken(t);
      try { setUser(JSON.parse(u)); } catch (e) { /* ignore */ }
      // verify in background
      axios
        .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } })
        .then((res) => {
          setUser(res.data);
          localStorage.setItem(USER_KEY, JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setToken(null);
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Link this device's notifications to whoever is signed in.
  useEffect(() => { if (user?.id) syncPush(); }, [user?.id]);

  const persist = (tok, usr) => {
    localStorage.setItem(TOKEN_KEY, tok);
    localStorage.setItem(USER_KEY, JSON.stringify(usr));
    setToken(tok);
    setUser(usr);
  };

  const signup = useCallback(async ({ name, email, password, handle, phone }) => {
    const res = await axios.post(`${API}/auth/signup`, { name, email, password, handle, phone });
    persist(res.data.token, res.data.user);
    return res.data.user;
  }, []);

  const login = useCallback(async ({ email, password }) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    persist(res.data.token, res.data.user);
    return res.data.user;
  }, []);

  const changePassword = useCallback(async (new_password) => {
    const res = await axios.post(`${API}/auth/change-password`, { new_password }, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
    persist(res.data.token, res.data.user);
  }, []);

  const resetPassword = useCallback(async ({ email, new_password }) => {
    const res = await axios.post(`${API}/auth/reset-password`, { email, new_password });
    persist(res.data.token, res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    unlinkPush(localStorage.getItem(TOKEN_KEY));
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const requestTrial = useCallback(async ({ email, name, source }) => {
    const res = await axios.post(`${API}/trial`, { email, name, source });
    return res.data;
  }, []);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (!t) return null;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${t}` } });
      setUser(res.data);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data));
      return res.data;
    } catch { return null; }
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, signup, login, logout, resetPassword, changePassword, requestTrial, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
